import { z } from 'zod';
import {
  PACKAGE_TIER_KEYS,
  type PlatformVerticalKey,
} from '../catalog/platformCatalogIds';
import type { PageMeta } from '../content/pageMeta';
import { resolveFamilyRecipeForTheme } from '../theme/themeFamily';
import { FOOTER_LEGAL_PAGE_SLUGS } from './footerLegalLinks';
import { PAGE_SLUG } from './pageRouting';

export const SITE_MODE = {
  minimal: 'minimal',
  standard: 'standard',
} as const;

export const SITE_MODE_KEY_VALUES = [SITE_MODE.minimal, SITE_MODE.standard] as const;

export type SiteMode = (typeof SITE_MODE_KEY_VALUES)[number];
export const SiteModeSchema = z.enum(SITE_MODE_KEY_VALUES);

export const MINIMAL_BOOTSTRAP_PAGE_SLUGS = [
  PAGE_SLUG.index,
  PAGE_SLUG.impressum,
  PAGE_SLUG.datenschutz,
] as const;

/** Standard pages shared by all verticals (no dedicated Menu route). */
export const STANDARD_BOOTSTRAP_PAGE_SLUGS = [
  PAGE_SLUG.index,
  PAGE_SLUG.aboutUs,
  PAGE_SLUG.contact,
  PAGE_SLUG.impressum,
  PAGE_SLUG.datenschutz,
] as const;

/** Restaurant standard — includes Speisekarte `/menu` (home keeps highlights only). */
export const RESTAURANT_STANDARD_BOOTSTRAP_PAGE_SLUGS = [
  PAGE_SLUG.index,
  PAGE_SLUG.menu,
  PAGE_SLUG.aboutUs,
  PAGE_SLUG.contact,
  PAGE_SLUG.impressum,
  PAGE_SLUG.datenschutz,
] as const;

const LEGAL_SLUG_SET = new Set<string>(FOOTER_LEGAL_PAGE_SLUGS);

/** Resolves site mode from stored config, falling back to package tier. */
export function resolveSiteMode(config: {
  packageTier?: string;
  siteMode?: SiteMode;
}): SiteMode {
  if (config.siteMode) {
    return config.siteMode;
  }
  if (config.packageTier === PACKAGE_TIER_KEYS.minimum) {
    return SITE_MODE.minimal;
  }
  return SITE_MODE.standard;
}

/**
 * Bootstrap page set for a site mode (+ vertical — restaurant adds `/menu`).
 * When `themeId` is set, family recipe `pages` intersects the base set
 * (Imbiss omits `/about-us`). Legal slugs always survive the filter.
 */
export function getBootstrapPageSlugs(
  siteMode: SiteMode,
  vertical?: string,
  themeId?: string,
): readonly string[] {
  let base: readonly string[];
  if (siteMode === SITE_MODE.minimal) {
    base = MINIMAL_BOOTSTRAP_PAGE_SLUGS;
  } else if (vertical === 'restaurant') {
    base = RESTAURANT_STANDARD_BOOTSTRAP_PAGE_SLUGS;
  } else {
    base = STANDARD_BOOTSTRAP_PAGE_SLUGS;
  }

  if (!themeId || !vertical) {
    return base;
  }

  const recipe = resolveFamilyRecipeForTheme(themeId, vertical as PlatformVerticalKey);
  if (!recipe.pages) {
    return base;
  }

  const allow = new Set<string>(recipe.pages);
  for (const legal of FOOTER_LEGAL_PAGE_SLUGS) {
    allow.add(legal);
  }
  return base.filter((slug) => allow.has(slug));
}

/** Publish bake set: minimal omits extra content routes (e.g. legacy about-us). */
export function filterPagesForPublish(pages: PageMeta[], siteMode: SiteMode): PageMeta[] {
  if (siteMode === SITE_MODE.standard) {
    return pages;
  }
  const allowed = new Set<string>(MINIMAL_BOOTSTRAP_PAGE_SLUGS);
  return pages.filter((page) => allowed.has(page.slug));
}

const MINIMAL_SLUG_TO_ANCHOR: Record<string, string> = {
  [PAGE_SLUG.aboutUs]: '#about',
  [PAGE_SLUG.menu]: '#menu',
  [PAGE_SLUG.contact]: '#contact',
};

/** Homepage anchor for unknown minimal-mode content slugs. */
export function resolveMinimalSlugAnchor(slug: string): string {
  return MINIMAL_SLUG_TO_ANCHOR[slug] ?? '#top';
}

/** True when a slug should redirect to the homepage anchor in minimal mode. */
export function shouldRedirectMinimalContentSlug(siteMode: SiteMode, slug: string): boolean {
  if (siteMode !== SITE_MODE.minimal) {
    return false;
  }
  if (slug === PAGE_SLUG.index) {
    return false;
  }
  if (LEGAL_SLUG_SET.has(slug)) {
    return false;
  }
  return true;
}
